import CampaignQuestIdModal from 'components/CampaignQuestIdModal'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputField from 'components/InputField'
import InputFieldPassword from 'components/InputFieldPassword'
import * as React from 'react'
import { Button } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { SummaryDocumentKindType } from 'api/common/constants'
import { buildCampaignSignUpConfirmUrl } from 'constants/routes'
import { scrollToAnchor } from 'utilities/helper'
import { campaignSignUpFormItems } from 'utilities/resources/formItems'
import {
  ErrorChecker,
  FormErrorMessage,
  atemQuestIdValidator,
  birthdayValidator,
  emailValidator,
  emptyValidator,
  passwordValidator,
} from 'utilities/validators'

const LOGIN_PATH = '/login' as const

export interface CampaignSignUpFormValues {
  email: string
  firstName: string
  lastName: string
  birthday: string
  password: string
  passwordConfirm: string
  atemQuestId: string
  /** Rev.20 S46: 概要書面交付方法 (SummaryElectronic / SummaryPaper から伝搬) */
  summaryDocumentKind?: SummaryDocumentKindType
  /** Rev.20 S46: 紙面選択時の概要書面番号 (SummaryPaper から伝搬) */
  summaryDocumentNumbers?: string[]
}

const initialFormValues = (prev?: CampaignSignUpFormValues): CampaignSignUpFormValues => ({
  email: prev?.email ?? '',
  firstName: prev?.firstName ?? '',
  lastName: prev?.lastName ?? '',
  birthday: prev?.birthday ?? '',
  password: prev?.password ?? '',
  passwordConfirm: prev?.passwordConfirm ?? '',
  atemQuestId: prev?.atemQuestId ?? '',
  summaryDocumentKind: prev?.summaryDocumentKind,
  summaryDocumentNumbers: prev?.summaryDocumentNumbers,
})

type LocationState = { formValues?: CampaignSignUpFormValues }

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { hash } = useParams()
  const prevState = (location.state ?? {}) as LocationState
  const [formValues, setFormValues] = React.useState<CampaignSignUpFormValues>(initialFormValues(prevState.formValues))
  const [showQuestModal, setShowQuestModal] = React.useState<boolean>(false)
  const [errorValues, setErrorValues] = React.useState<FormErrorMessage<CampaignSignUpFormValues>>()
  const errorChecker = React.useMemo(() => new ErrorChecker<CampaignSignUpFormValues>(campaignSignUpFormItems), [])

  React.useEffect(() => {
    if (!hash) {
      navigate(LOGIN_PATH)
    }
  }, [hash, navigate])

  const setField = <K extends keyof CampaignSignUpFormValues>(key: K, value: CampaignSignUpFormValues[K]): void => {
    setFormValues((prev) => ({ ...prev, [key]: value }))
  }

  const handleQuestIdComplete = (atemQuestId: string): void => {
    setField('atemQuestId', atemQuestId)
    setShowQuestModal(false)
  }

  /**
   * 確認画面遷移前の FE バリデーション (API 呼出抑止用)
   * @returns 全項目 valid なら true / いずれか invalid なら false
   */
  const runFrontEndValidation = (): boolean => {
    errorChecker.init()
    errorChecker.check(emailValidator(formValues.email), 'email')
    errorChecker.check(emptyValidator(formValues.lastName), 'lastName')
    errorChecker.check(emptyValidator(formValues.firstName), 'firstName')
    errorChecker.check(birthdayValidator(formValues.birthday), 'birthday')
    errorChecker.check(passwordValidator(formValues.password), 'password')
    errorChecker.check(passwordValidator(formValues.passwordConfirm, formValues.password), 'passwordConfirm')
    errorChecker.check(atemQuestIdValidator(formValues.atemQuestId), 'atemQuestId')
    setErrorValues(errorChecker.getErrorMessageData())
    return errorChecker.isValid()
  }

  const handleConfirmClick = (): void => {
    if (!hash) return
    if (!runFrontEndValidation()) {
      scrollToAnchor(errorChecker.getFirstInvalidItemId())
      return
    }
    navigate(buildCampaignSignUpConfirmUrl(hash), { state: { formValues } })
  }

  const canOpenQuestModal = formValues.email.length > 0
  const allRequiredEntered = errorChecker.isAllEntered(formValues, ['must'])

  if (!hash) {
    return null
  }

  return (
    <>
      <Header title="仮登録" />
      <main>
        <section className="mx-4 my-5">
          <form>
            <InputField
              name="email"
              type="email"
              labelTop="メールアドレス"
              value={formValues.email}
              setValue={(v) => setField('email', v)}
              placeholder="user@example.com"
              errors={[errorValues?.email ?? '']}
              bold
            />
            <div className="mt-3 d-flex gap-3">
              <InputField
                name="last-name"
                type="text"
                labelTop="姓"
                value={formValues.lastName}
                setValue={(v) => setField('lastName', v)}
                placeholder="山田"
                errors={[errorValues?.lastName ?? '']}
                bold
              />
              <InputField
                name="first-name"
                type="text"
                labelTop="名"
                value={formValues.firstName}
                setValue={(v) => setField('firstName', v)}
                placeholder="太郎"
                errors={[errorValues?.firstName ?? '']}
                bold
              />
            </div>
            <InputField
              name="birthday"
              type="number"
              labelTop="生年月日（西暦）"
              value={formValues.birthday}
              setValue={(v) => setField('birthday', v)}
              placeholder="19900625"
              notes={['※半角数字で入力してください (例: 19900625)']}
              errors={[errorValues?.birthday ?? '']}
              className="mt-3"
              bold
            />
            <InputFieldPassword
              name="password"
              type="password"
              label="ログインパスワード"
              value={formValues.password}
              setValue={(v) => setField('password', v)}
              placeholder="password"
              notes={['※半角英数字を含む文字で入力してください', '※大文字と小文字を含む文字で入力してください', '※8文字以上で入力してください']}
              errors={[errorValues?.password ?? '']}
              className="mt-3"
              bold
            />
            <InputFieldPassword
              name="password-confirm"
              type="password"
              label="パスワード確認"
              value={formValues.passwordConfirm}
              setValue={(v) => setField('passwordConfirm', v)}
              placeholder="password"
              errors={[errorValues?.passwordConfirm ?? '']}
              className="mt-3"
              bold
            />

            <div id={`input_item_id_${campaignSignUpFormItems.atemQuestId.name}`} className="mt-4 p-3 border rounded">
              <p className="fw-bold mb-2">クエストID</p>
              <p className="mb-2">{formValues.atemQuestId || '未取得'}</p>
              <Button variant="outline-primary" onClick={() => setShowQuestModal(true)} disabled={!canOpenQuestModal}>
                クエストID取得
              </Button>
              {!canOpenQuestModal && <p className="note text-danger mt-2">メールアドレスを入力してから取得してください。</p>}
              {errorValues?.atemQuestId && <p className="note text-danger mt-2">{errorValues.atemQuestId}</p>}
            </div>

            <div className="mt-5">
              <Button variant="primary" className="w-100 py-4" onClick={handleConfirmClick} disabled={!allRequiredEntered}>
                確認画面へ
              </Button>
            </div>
          </form>
        </section>
      </main>
      <Footer className="mt-5" />
      <CampaignQuestIdModal
        show={showQuestModal}
        onHide={() => setShowQuestModal(false)}
        onComplete={handleQuestIdComplete}
        formEmail={formValues.email}
        formName={`${formValues.lastName} ${formValues.firstName}`.trim()}
        formPassword={formValues.password}
        hash={hash}
      />
    </>
  )
}

export default SceneComponent
